import {
  MilestoneRoleDisplay,
  MilestoneUser,
  MilestoneUsersContainer,
  ProjectLeaderRoleDisplay,
} from "@/containers/Project/components";
import * as GlobalS from "@/containers/Project/components/styled";
import { FieldNames, FormType } from "@/enums";
import { projectMilestoneType } from "@/propTypes";
import {
  useFormPropertyValueLookupProvider,
  useProjectContext,
} from "@/providers";
import { useGetFormLookupsQuery } from "@/services";
import { Typography } from "antd";
import _ from "lodash";
import { bool, object, string } from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";

const { Title } = Typography;

export const MilestoneApprover = ({
  milestone,
  region,
  shouldShowEditView,
  form,
}) => {
  const intl = useIntl();
  const { data: forms } = useGetFormLookupsQuery();
  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();
  const { project } = useProjectContext();
  const { projectLeaders } = project || {};

  const [regions, setRegions] = useState();
  const [rolesByFunctionalArea, setRolesByFunctionalArea] = useState();

  const approversFilter = useCallback(
    (approver) =>
      approver.regionId === region &&
      (shouldShowEditView || !approver.isNotApplicable),
    [region, shouldShowEditView]
  );

  useEffect(() => {
    const approverRoles = milestone.approvers?.filter(approversFilter);
    const rolesGroupByFunctionalArea = _.groupBy(
      approverRoles,
      (x) => x.functionalAreaId
    );

    setRolesByFunctionalArea(
      Object.keys(rolesGroupByFunctionalArea)
        .filter((role) => !role.assignedUsers?.length)
        .map((functionalAreaId) => rolesGroupByFunctionalArea[functionalAreaId])
        .flat()
    );
  }, [approversFilter, milestone, region]);

  useEffect(() => {
    setRegions(forms?.intake?.region);
  }, [forms?.intake]);

  const regionLeaderApprover =
    milestone.projectLeaderApprovers?.find(approversFilter);

  const hasAssignedApprovers = milestone.approvers
    ?.filter(approversFilter)
    ?.some((role) => !!role.assignedUsers?.length);

  const isEmptyRegion = milestone.approvers
    ?.filter(approversFilter)
    ?.every(
      (role) => !role.assignedUsers?.length && role.isNotApplicable !== true
    );

  if (shouldShowEditView) {
    if (isEmptyRegion) {
      return null;
    }
  } else {
    if (
      !hasAssignedApprovers &&
      !projectLeaders[regionLeaderApprover?.regionId]
    ) {
      return null;
    }
  }

  const renderProjectLeaderApproverView = () => {
    if (!regionLeaderApprover) return null;

    return shouldShowEditView ? (
      <ProjectLeaderRoleDisplay role={regionLeaderApprover} form={form} />
    ) : (
      <MilestoneUsersContainer
        title={intl.formatMessage({
          id: "project.approvers.projectLeader",
        })}
        subTitle={intl.formatMessage({ id: "project.approvers.user" })}
      >
        {[
          {
            ...(regionLeaderApprover?.assignedUser || {}),
            userId: projectLeaders[regionLeaderApprover?.regionId],
          },
        ].map((user) => (
          <MilestoneUser
            key={user.userId}
            user={user}
            milestone={milestone}
            dataTestId={`milestone-approver-milestone-${milestone.id}-user-${user.userId}`}
          />
        ))}
      </MilestoneUsersContainer>
    );
  };

  return (
    <GlobalS.RegionDisplayCol flex="1">
      <Title level={4}>{(regions || {})[region]?.fallback}</Title>

      {shouldShowEditView ? (
        <MilestoneRoleDisplay
          region={region}
          roles={rolesByFunctionalArea}
          form={form}
          editMode={true}
        />
      ) : (
        rolesByFunctionalArea &&
        rolesByFunctionalArea.map((role) => {
          const title = getPropertyValueDisplay(
            FormType.Intake,
            FieldNames.FunctionalArea,
            role.functionalAreaId
          );

          return (
            <MilestoneUsersContainer
              key={role.id}
              title={title}
              subTitle={role.name}
            >
              {role.assignedUsers.map((user) => (
                <MilestoneUser
                  key={user.userId}
                  user={user}
                  milestone={milestone}
                  dataTestId={`milestone-approver-milestone-${milestone.id}-role-${role.id}-user-${user.userId}`}
                />
              ))}
            </MilestoneUsersContainer>
          );
        })
      )}
      {renderProjectLeaderApproverView()}
    </GlobalS.RegionDisplayCol>
  );
};

MilestoneApprover.propTypes = {
  milestone: projectMilestoneType.isRequired,
  region: string.isRequired,
  shouldShowEditView: bool,
  form: object,
};
